import { useMutation } from '@tanstack/react-query'
import type { AxiosError } from 'axios'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'


import {
  setUser,
  setAccessToken,
  setRefreshToken,
  setPermissions,
  clearUser,
  setLoading,
  setError,
  resetAuthState,
} from '../../../store/slices/authSlice'
import {
  loginWithPassword as loginWithPasswordService,
  verifyTotp as verifyTotpService,
  logout as logoutService,
  getStoredUser,
} from '../services/index.service'
import type { LoginStepResponse, VerifyTotpResult, VerifyOTPTokenPayload } from '../types'

import { queryClient } from '@/api/queryClient'
import { persistor } from '@/store'

export const useAuth = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  // Đưa admin vừa đăng nhập thành công vào Redux. Dùng chung cho hai nơi
  // đăng nhập có thể hoàn tất: bước 1 khi TOTP tắt (local/dev), và bước 2
  // sau khi xác thực TOTP thành công. Không dùng chung với logout — 6 bước
  // này phải chạy tuần tự để tránh lẫn quyền/permissions của admin trước đó.
  const completeLogin = async (tokenData: VerifyOTPTokenPayload) => {
    dispatch(resetAuthState())
    queryClient.clear()
    await persistor.purge()
    dispatch(setAccessToken(tokenData.access_token))
    dispatch(setRefreshToken(tokenData.refresh_token))
    const storedUser = getStoredUser()
    if (storedUser) {
      dispatch(
        setUser({
          id: storedUser.id,
          username: storedUser.phone,
          fullName: storedUser.full_name || storedUser.phone,
          email: storedUser.email || '',
          phoneNumber: storedUser.phone,
          avatar: storedUser.avatar,
          roles: storedUser.roles || [],
          permissions: [],
          isSuperAdmin: false,
        })
      )
    }
    dispatch(
      setPermissions({
        permissions: tokenData.permissions ?? [],
        isSuperAdmin: tokenData.is_super_admin ?? false,
      })
    )
  }

  const logoutMutation = useMutation({
    mutationFn: () => logoutService(),
    onSuccess: async () => {
      dispatch(clearUser())
      // Drop every cached query so a same-tab login as a different admin
      // doesn't read this admin's `['me']` (or any other) cache.
      queryClient.clear()
      // Purge persisted state to ensure clean logout
      await persistor.purge()
      navigate('/login')
    },
  })

  // Bước 1: xác thực username + mật khẩu.
  const loginWithPasswordMutation = useMutation<
    LoginStepResponse,
    AxiosError,
    { username: string; password: string }
  >({
    mutationFn: ({ username, password }) => loginWithPasswordService(username, password),
    onMutate: () => {
      dispatch(setLoading(true))
      dispatch(setError(null))
    },
    onSuccess: async (data) => {
      // TOTP tắt (local/dev): backend đã cấp token luôn ở bước 1, hoàn tất
      // đăng nhập ngay — không có bước 2.
      if (!data.mfa_required && data.auth) {
        await completeLogin(data.auth)
      }
      dispatch(setLoading(false))
    },
    onError: (error) => {
      const errorMessage = (error.response?.data as any)?.message || error.message || 'Đăng nhập thất bại'
      dispatch(setError(errorMessage))
      dispatch(setLoading(false))
    },
  })

  // Bước 2: xác thực mã TOTP bằng mfa_token nhận được ở bước 1.
  const verifyTotpMutation = useMutation<
    VerifyTotpResult,
    AxiosError,
    { mfaToken: string; code: string }
  >({
    mutationFn: ({ mfaToken, code }) => verifyTotpService(mfaToken, code),
    onMutate: () => {
      dispatch(setLoading(true))
      dispatch(setError(null))
    },
    onSuccess: async (data) => {
      if (data.status === 200 && (!data.error_code || data.error_code === 'SUCCESS') && data.data) {
        await completeLogin(data.data)
        dispatch(setLoading(false))
        // Navigation will be handled by LoginPage based on isAuthenticated state change
      } else {
        dispatch(setError(data.message || 'Xác thực không thành công'))
        dispatch(setLoading(false))
      }
    },
    onError: (error) => {
      const errorMessage = (error.response?.data as any)?.message || error.message || 'Xác thực không thành công'
      dispatch(setError(errorMessage))
      dispatch(setLoading(false))
    },
  })

  return {
    loginWithPassword: loginWithPasswordMutation.mutate,
    isLoggingInWithPassword: loginWithPasswordMutation.isPending,
    loginWithPasswordError: loginWithPasswordMutation.error,
    loginWithPasswordSuccess: loginWithPasswordMutation.isSuccess,
    loginWithPasswordData: loginWithPasswordMutation.data,

    verifyTotp: verifyTotpMutation.mutate,
    isVerifyingTotp: verifyTotpMutation.isPending,
    verifyTotpError: verifyTotpMutation.error,
    verifyTotpSuccess: verifyTotpMutation.isSuccess,

    logout: logoutMutation.mutate,
    isLoggingOut: logoutMutation.isPending,
  }
}

export default useAuth
